import {
  Building,
  CheckCircle,
  Circle,
  Clock,
  Flag,
  Inbox,
  Mail,
  Paperclip,
  Plus,
  Search,
  Star,
  Tag,
  Trash2,
  User
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface Message {
  id: number;
  from: string;
  fromEmail: string;
  subject: string;
  preview: string;
  content: string;
  timestamp: string;
  isRead: boolean;
  isStarred: boolean;
  isImportant: boolean;
  hasAttachment: boolean;
  avatar?: string;
  assignedTo?: string;
  status: 'open' | 'closed' | 'pending';
  priority: 'low' | 'medium' | 'high';
  tags?: string[];
  company?: string;
  lastActivity: string;
  thread?: ThreadItem[];
}

interface ThreadItem {
  id: number;
  type: 'email' | 'comment';
  from: string;
  fromEmail?: string;
  content: string;
  timestamp: string;
  avatar: string;
  isSent?: boolean;
}

interface InboxFilter {
  id: string;
  label: string;
  icon: React.ComponentType<any>;
  count: number;
  active: boolean;
}

const messages: Message[] = [
  {
    id: 1,
    from: 'Tanmay Sharma',
    fromEmail: 'tanmay@growthwave.com',
    subject: 'Quick Turnaround Needed — TechNet Solutions "Green Horizons" Brief & Services Request Form',
    preview: 'Hi Alan, Could you take point on completing Professional Services Request Form REQ-2025-5794 for our new TechNet Solutions engagement?',
    content: `Hi Alan,

Could you take point on completing Professional Services Request Form REQ-2025-5794 for our new TechNet Solutions engagement?
Melissa Green (their Marketing Director) wants the paperwork before she boards a flight tomorrow, so I'd like the form submitted by EOD today if possible.

Below is the full briefing from my call with Melissa. Everything you need to slot into the form is tucked inside — company details, timeline, budget bands, deliverables, tech specs, terms, you name it. Feel free to ping me if anything's unclear, but this should save you digging through Slack threads.

⸻

Kick-off Briefing – TechNet Solutions "Green Horizons" Brand Relaunch
15 July 2025 – compiled by Tanmay Sharma

I just got out of a marathon call with Melissa Green over at TechNet Solutions, and I want to get everyone up to speed before the weekend slips away. If you skim nothing else, know that TechNet Solutions is dead-set on unveiling their "Green Horizons" brand relaunch eight weeks from next Monday—no wiggle room—because their distributor resets shelves the second week of September.

⸻

What TechNet Solutions actually needs (between the lines)
• They want a soup-to-nuts integrated marketing push: refreshed identity, three snappy 15-second spots, a pile of static & vertical cut-downs for socials, and an influencer package (think micro-creators who brew, hike, and compost on TikTok).
• Budget was floated at "mid-thirty to just under fifty if the numbers make sense," with an email from their finance controller confirming the upper limit is $50k all-in.
• Timing? The phrase "July 22nd drop-dead kickoff, September 16th final asset hand-off by 5 p.m. Pacific" got repeated often enough that I wrote it on the whiteboard twice.
• KPIs: 5% CTR on paid social and 20% traffic uplift vs. baseline in the first 30 days.
• Creative must export flawlessly to 1080×1080, 1080×1350, 1920×1080, layered PSD/AI source files. We'll design in Figma, route reviews through Frame.io, and track tasks in Asana.
• Data push: nightly webhook to TechNet Solutions's Looker Studio dashboard; sync ad audiences with Klaviyo.

⸻

Critical breadcrumbs for the form
• Company: TechNet Solutions • Address: 123 Greenway Rd, San Francisco CA 94107
• Contact: Melissa Green, Director of Marketing • Phone: +1 415 555-0199 • Email: mgreen@technetsolutions.com
• Project title: "Green Horizons Brand Relaunch" • Service type: Integrated Marketing Campaign
• Urgency level: High • Request date: 15 July 2025
• Timeline: 22 Jul → 16 Sep 2025 (asset drop 16 Sep, 17:00 PDT)
• Budget range: $35,000 – $50,000 (includes $6k onboarding, $6k/mo retainer × 6)
• Scope: Concept, design & production of new branding; three 15-sec video ads; social asset suite; influencer kit; 30-day performance report.
• Deliverables: Brand guidelines PDF; 3× 15-sec spots; 30× social assets; influencer brief deck; performance report.
• Technical reqs: Layered source files; multi-size exports.
• Platform prefs: Figma, Frame.io, Asana.
• Integration needs: Looker Studio webhook; Klaviyo audience sync.
• Performance reqs: 5% CTR, 20% traffic lift.
• Payment terms: Net 30 • Boxes to tick: Ongoing Support Required (no warranty/training).
• Authorized signer: Melissa Green – signature expected 15 Jul 2025.

⸻

Tone & urgency
Melissa labeled the project "mission-critical, high urgency." Contract must leave our outbox before COB tomorrow or shelf space vanishes.

Hidden-in-plain-sight numbers
Onboarding fee and retainer totals baked into the range; media spend handled separately.

Last mile
After e-signature, deal moves to Closed-Won; remember to tick Ongoing Support so post-launch analytics hours don't slip through the cracks.

(If you're still hunting for a timeline or phone number, go back—Melissa handed us everything between the lines.)

⸻

Thanks for turning this around, Alan. Shoot me a Slack if any field feels ambiguous.

Best,
Tanmay

—
GrowthWave | Account Strategy`,
    timestamp: '2 hours ago',
    isRead: false,
    isStarred: false,
    isImportant: true,
    hasAttachment: false,
    avatar: 'TS',
    assignedTo: 'Alan Chen',
    status: 'open',
    priority: 'high',
    tags: ['urgent', 'form-required'],
    company: 'TechNet Solutions',
    lastActivity: '2 hours ago',
    thread: []
  },
  {
    id: 2,
    from: 'System Notification',
    fromEmail: 'noreply@crm.com',
    subject: 'Task Assignment: Complete Professional Service Request Form',
    preview: 'A new task has been assigned to you: Complete Professional Service Request Form for TechNet Solutions LLC',
    content: `A new task has been assigned to you:

Task: Complete Professional Service Request Form
Client: TechNet Solutions LLC
Due Date: Today at 5:00 PM
Priority: High

Please complete this task by clicking on it in your Tasks dashboard.`,
    timestamp: '4 hours ago',
    isRead: true,
    isStarred: false,
    isImportant: false,
    hasAttachment: false,
    avatar: 'SN',
    assignedTo: 'You',
    status: 'pending',
    priority: 'high',
    tags: ['system', 'task'],
    company: 'TechNet Solutions',
    lastActivity: '4 hours ago',
    thread: []
  },
  {
    id: 3,
    from: 'Melissa Green',
    fromEmail: 'mgreen@technetsolutions.com',
    subject: 'Re: TechNet Solutions Partnership Discussion',
    preview: 'Thanks for the great call today! Looking forward to working with GrowthWave on our Green Horizons campaign.',
    content: `Hi Tanmay,

Thanks for the great call today! Looking forward to working with GrowthWave on our Green Horizons campaign.

As discussed, we need to move quickly on this. Our distributor deadline is non-negotiable, so having the paperwork completed by tomorrow would be ideal.

I'll be available via email until 6 PM PST tomorrow, then I'll be traveling.

Best regards,
Melissa Green
Director of Marketing
TechNet Solutions LLC`,
    timestamp: '6 hours ago',
    isRead: true,
    isStarred: true,
    isImportant: false,
    hasAttachment: false,
    avatar: 'MG',
    assignedTo: 'Tanmay Sharma',
    status: 'closed',
    priority: 'medium',
    tags: ['client', 'follow-up'],
    company: 'TechNet Solutions',
    lastActivity: '6 hours ago',
    thread: [
      {
        id: 1,
        type: 'email',
        from: 'Tanmay Sharma',
        fromEmail: 'tanmay@thetasoftware.ai',
        content: 'Hey',
        timestamp: '11:25 PM',
        avatar: 'TS',
        isSent: true
      },
      {
        id: 2,
        type: 'email',
        from: 'Tanmay Sharma',
        fromEmail: 'tanmay@thetasoftware.ai',
        content: 'hello',
        timestamp: '4:38 PM',
        avatar: 'TS',
        isSent: true
      }
    ]
  },
  // Elise follow-up emails matching the activity feed
  {
    id: 4,
    from: 'Elise Rodriguez',
    fromEmail: 'elise.rodriguez@innovatech.com',
    subject: 'Follow up',
    preview: 'Hi there! Just wanted to follow up on our conversation about the digital transformation project...',
    content: `Hi there!

Just wanted to follow up on our conversation about the digital transformation project we discussed last week.

Our team at InnovaTech has been reviewing the proposal you sent, and we're very interested in moving forward. We particularly liked your approach to the cloud migration strategy and the timeline you outlined.

A few questions:
1. Can we schedule a technical deep-dive session with our engineering team?
2. What would be the earliest possible start date?
3. Do you have case studies from similar manufacturing companies?

I'd love to set up a call this week to discuss next steps. I'm available Tuesday through Thursday between 2-4 PM EST.

Looking forward to hearing from you!

Best regards,
Elise Rodriguez
VP of Technology
InnovaTech Solutions
Phone: (555) 123-7890
elise.rodriguez@innovatech.com`,
    timestamp: 'Today at 12:28 PM',
    isRead: true,
    isStarred: false,
    isImportant: false,
    hasAttachment: false,
    avatar: 'ER',
    assignedTo: 'You',
    status: 'open',
    priority: 'medium',
    tags: ['follow-up', 'technical'],
    company: 'InnovaTech Solutions',
    lastActivity: 'Today at 12:29 PM',
    thread: [
      {
        id: 3,
        type: 'comment',
        from: 'You',
        content: 'Email opened at 12:29 PM - good engagement timing. Need to respond with technical team availability.',
        timestamp: '12:29 PM',
        avatar: 'YU'
      }
    ]
  },
  // Sofia marketing advice email
  {
    id: 5,
    from: 'Sofia Sulikowski',
    fromEmail: 'sofia.sulikowski@marketingpro.com',
    subject: 'Re: Marketing Job Advice Part 2',
    preview: 'Thank you so much for the detailed advice on transitioning from traditional to digital marketing...',
    content: `Hi!

Thank you so much for the detailed advice on transitioning from traditional to digital marketing. Your insights about the importance of data analytics and customer journey mapping really resonated with me.

I've been implementing some of the strategies you mentioned:

1. Started learning Google Analytics 4 - the interface is quite different from Universal Analytics!
2. Set up my first automated email sequence using Mailchimp
3. Created a content calendar for LinkedIn posts (posting 3x per week now)

The results have been encouraging. My LinkedIn engagement has increased by 40% in just two weeks, and I'm starting to see more inbound inquiries.

I do have a follow-up question about attribution modeling - how do you typically handle multi-touch attribution for B2B campaigns? The customer journey seems so complex with multiple touchpoints across different channels.

Also, would you be open to a brief call sometime? I'd love to pick your brain about marketing automation tools. I'm currently evaluating HubSpot vs. Pardot for our mid-market segment.

Thanks again for all your help!

Best,
Sofia

Sofia Sulikowski
Marketing Specialist
MarketingPro Consulting
sofia.sulikowski@marketingpro.com
(555) 234-5678`,
    timestamp: 'December 3',
    isRead: true,
    isStarred: false,
    isImportant: false,
    hasAttachment: false,
    avatar: 'SS',
    assignedTo: 'You',
    status: 'closed',
    priority: 'low',
    tags: ['advice', 'marketing', 'networking'],
    company: 'MarketingPro Consulting',
    lastActivity: 'December 3',
    thread: [
      {
        id: 4,
        type: 'comment',
        from: 'You',
        content: 'Great progress on her marketing skills development. This person has opened the email 7 times - very engaged. Consider offering a mentorship or consulting arrangement.',
        timestamp: 'December 3',
        avatar: 'YU'
      }
    ]
  },
  // Shreyas name correction email
  {
    id: 6,
    from: 'Shreyas Patel',
    fromEmail: 'shreyas.patel@techstartup.io',
    subject: 'Re: Name correction',
    preview: 'Hi, thanks for getting back to me about the contract. I noticed my name was misspelled...',
    content: `Hi,

Thanks for getting back to me about the contract. I noticed my name was misspelled as "Shreyas" in a few places in the agreement - the correct spelling is "Shreyas" (no change needed actually, but wanted to double-check the pronunciation guide for our upcoming calls).

For reference:
- Pronunciation: SHRAY-us (like "shray" + "us")
- Email signature should remain: Shreyas Patel

Also, while I have your attention, I wanted to confirm a few details about the software implementation timeline:

1. Phase 1 kickoff is still scheduled for January 15th, correct?
2. Will we need to coordinate with your DevOps team for the staging environment setup?
3. Should we plan for any downtime during the migration window?

Our development team is excited to start working with you. The technical specifications you provided were very thorough.

Best regards,
Shreyas Patel
CTO & Co-founder
TechStartup.io
shreyas.patel@techstartup.io
Direct: (555) 345-9876`,
    timestamp: 'November 25',
    isRead: true,
    isStarred: false,
    isImportant: false,
    hasAttachment: false,
    avatar: 'SP',
    assignedTo: 'You',
    status: 'closed',
    priority: 'low',
    tags: ['contract', 'implementation'],
    company: 'TechStartup.io',
    lastActivity: 'November 25',
    thread: [
      {
        id: 5,
        type: 'comment',
        from: 'You',
        content: 'Name correction handled. Implementation timeline confirmed. This email was opened 2 times - standard engagement.',
        timestamp: 'November 25',
        avatar: 'YU'
      }
    ]
  },
  // Additional realistic emails
  {
    id: 7,
    from: 'David Chen',
    fromEmail: 'david.chen@globalcorp.com',
    subject: 'Q4 Budget Approval - Enterprise Software License',
    preview: 'The finance committee has approved the budget for the enterprise software licensing project...',
    content: `Hi team,

Great news! The finance committee has approved the budget for the enterprise software licensing project we discussed.

Approved Budget: $280,000
Timeline: Q1 2025 implementation
Scope: 500+ user licenses + implementation services

Next steps:
1. Legal review of the master service agreement (should take 1-2 weeks)
2. Technical requirements validation with our IT security team
3. Finalize implementation timeline and resource allocation

I'll be your primary point of contact moving forward. My assistant Sarah will send calendar invites for our kickoff meeting next week.

One quick question - do you offer any volume discounts for organizations over 1,000 employees? We might want to expand this to our European offices if the pilot goes well.

Looking forward to working together!

David Chen
VP of Operations
GlobalCorp Industries
david.chen@globalcorp.com
Office: (555) 678-9012`,
    timestamp: 'Yesterday at 3:45 PM',
    isRead: false,
    isStarred: true,
    isImportant: true,
    hasAttachment: false,
    avatar: 'DC',
    assignedTo: 'You',
    status: 'open',
    priority: 'high',
    tags: ['budget-approved', 'enterprise', 'high-value'],
    company: 'GlobalCorp Industries',
    lastActivity: 'Yesterday at 3:45 PM',
    thread: []
  },
  {
    id: 8,
    from: 'Jennifer Walsh',
    fromEmail: 'jennifer.walsh@healthtech.com',
    subject: 'Security Compliance Questions - HIPAA Requirements',
    preview: 'We need to ensure your platform meets our HIPAA compliance requirements before we can proceed...',
    content: `Hello,

We need to ensure your platform meets our HIPAA compliance requirements before we can proceed with the integration project.

Our compliance team has prepared a questionnaire covering:
- Data encryption standards (at rest and in transit)
- Access controls and audit logging
- Business Associate Agreement requirements
- Backup and disaster recovery procedures

Could you please:
1. Complete the attached security questionnaire
2. Provide your SOC 2 Type II report (if available)
3. Schedule a call with our security officer, Mike Rodriguez

Timeline: We need these materials by Friday to stay on track for our January go-live date.

This is a $150K project, so we're committed to making it work - just need to check all the compliance boxes first.

Thanks for your patience with our security requirements. Healthcare data is obviously very sensitive.

Best regards,
Jennifer Walsh
Director of IT Compliance
HealthTech Solutions
jennifer.walsh@healthtech.com
Direct: (555) 789-0123`,
    timestamp: 'Yesterday at 10:30 AM',
    isRead: true,
    isStarred: false,
    isImportant: true,
    hasAttachment: true,
    avatar: 'JW',
    assignedTo: 'You',
    status: 'open',
    priority: 'high',
    tags: ['compliance', 'security', 'healthcare'],
    company: 'HealthTech Solutions',
    lastActivity: 'Yesterday at 10:30 AM',
    thread: []
  },
  {
    id: 9,
    from: 'Marcus Thompson',
    fromEmail: 'marcus.thompson@retailchain.com',
    subject: 'Demo Feedback - Very Impressed!',
    preview: 'The demo yesterday was fantastic. Our executive team was very impressed with the inventory management features...',
    content: `Hi there,

The demo yesterday was fantastic. Our executive team was very impressed with the inventory management features, especially the real-time analytics dashboard.

Key highlights that resonated:
✓ Multi-location inventory tracking
✓ Automated reorder points
✓ Integration with our existing POS system
✓ Mobile app for store managers

A few questions came up during our internal debrief:
1. Can the system handle seasonal inventory adjustments automatically?
2. What's the typical implementation timeline for a 50-store chain?
3. Do you offer training for our store managers?

Budget-wise, we're looking at around $75K for the full implementation. Is that in the right ballpark?

I'd like to set up a technical deep-dive with our IT director, Lisa Park, sometime next week. She'll want to discuss API capabilities and data migration from our legacy system.

This could be a great partnership - we're planning to expand to 75 stores by end of 2025!

Cheers,
Marcus

Marcus Thompson
Operations Manager
RetailChain Plus
marcus.thompson@retailchain.com
Mobile: (555) 890-1234`,
    timestamp: '2 days ago',
    isRead: true,
    isStarred: false,
    isImportant: false,
    hasAttachment: false,
    avatar: 'MT',
    assignedTo: 'You',
    status: 'open',
    priority: 'medium',
    tags: ['demo-feedback', 'retail', 'expansion'],
    company: 'RetailChain Plus',
    lastActivity: '2 days ago',
    thread: []
  },
  {
    id: 10,
    from: 'Lisa Park',
    fromEmail: 'lisa.park@financialservices.net',
    subject: 'Contract Amendment - Additional User Licenses',
    preview: 'We need to add 25 more user licenses to our existing contract due to company growth...',
    content: `Hi,

We need to add 25 more user licenses to our existing contract due to company growth. Our headcount has increased faster than expected this quarter.

Current contract: 100 licenses @ $50/month each
Requested addition: 25 licenses
Preferred start date: October 1st

Questions:
1. Can we get the same pricing tier for the additional licenses?
2. Is there a volume discount available now that we'd be at 125 total users?
3. Will this extend our contract term, or can we keep the same renewal date?

Also, our CFO asked about annual payment options. We currently pay monthly but would consider switching to annual if there's a meaningful discount.

Please send over an amended contract when you have a chance. We're hoping to get this processed before month-end.

Thanks!

Lisa Park
Director of Finance
Financial Services Group
lisa.park@financialservices.net
Office: (555) 901-2345`,
    timestamp: '3 days ago',
    isRead: true,
    isStarred: false,
    isImportant: false,
    hasAttachment: false,
    avatar: 'LP',
    assignedTo: 'You',
    status: 'pending',
    priority: 'medium',
    tags: ['contract-amendment', 'expansion', 'existing-client'],
    company: 'Financial Services Group',
    lastActivity: '3 days ago',
    thread: []
  }
];

const inboxFilters: InboxFilter[] = [
  { id: 'unassigned', label: 'Unassigned', icon: Circle, count: 0, active: false },
  { id: 'assigned-to-me', label: 'Assigned to me', icon: User, count: 8, active: true },
  { id: 'all-open', label: 'All open', icon: Inbox, count: 6, active: false },
  { id: 'closed', label: 'Closed', icon: CheckCircle, count: 4, active: false },
];

const Messages: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null);
  const [messageList, setMessageList] = useState<Message[]>(messages);
  const [activeFilter, setActiveFilter] = useState<string>('assigned-to-me');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [replyText, setReplyText] = useState<string>('');
  const [activeCompositionTab, setActiveCompositionTab] = useState<'email' | 'comment'>('email');
  const [commentText, setCommentText] = useState<string>('');

  // Handle emailId query parameter from activity feed routing
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const emailId = searchParams.get('emailId');
    
    if (emailId) {
      const targetMessage = messages.find(msg => msg.id === parseInt(emailId));
      if (targetMessage) {
        setSelectedMessage(targetMessage);
        // Mark as read
        setMessageList(prev => 
          prev.map(msg => 
            msg.id === targetMessage.id ? { ...msg, isRead: true } : msg
          )
        );
        // Clear the query parameter to keep URL clean
        navigate('/messages', { replace: true });
      }
    }
  }, [location.search, navigate]);

  const handleMessageClick = (message: Message) => {
    setSelectedMessage(message);
    // Mark as read
    setMessageList(prev => 
      prev.map(msg => 
        msg.id === message.id ? { ...msg, isRead: true } : msg
      )
    );
  };

  const toggleStar = (messageId: number) => {
    setMessageList(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, isStarred: !msg.isStarred } : msg
      )
    );

    // Also update selected message if it's the same message
    if (selectedMessage && selectedMessage.id === messageId) {
      setSelectedMessage(prev => prev ? { ...prev, isStarred: !prev.isStarred } : null);
    }
  };

  const toggleImportant = (messageId: number) => {
    setMessageList(prev => 
      prev.map(msg => 
        msg.id === messageId ? { ...msg, isImportant: !msg.isImportant } : msg
      )
    );

    // Also update selected message if it's the same message
    if (selectedMessage && selectedMessage.id === messageId) {
      setSelectedMessage(prev => prev ? { ...prev, isImportant: !prev.isImportant } : null);
    }
  };

  const handleSendReply = () => {
    if (!selectedMessage || !replyText.trim()) return;

    // Create new thread item
    const newThreadItem: ThreadItem = {
      id: Date.now(),
      type: 'email',
      from: 'You',
      fromEmail: 'you@thetasoftware.ai',
      content: replyText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      avatar: 'YU',
      isSent: true
    };

    // Add to message thread and update message list
    setMessageList(prev => 
      prev.map(msg => 
        msg.id === selectedMessage.id 
          ? { 
              ...msg, 
              thread: [...(msg.thread || []), newThreadItem],
              lastActivity: 'just now'
            }
          : msg
      )
    );

    // Update selected message to show new thread item
    setSelectedMessage(prev => prev ? {
      ...prev,
      thread: [...(prev.thread || []), newThreadItem],
      lastActivity: 'just now'
    } : null);

    // Clear the reply text
    setReplyText('');

    console.log('Email sent:', {
      to: selectedMessage.fromEmail,
      subject: `Re: ${selectedMessage.subject}`,
      content: replyText
    });
  };

  const handleAddComment = () => {
    if (!selectedMessage || !commentText.trim()) return;

    // Create new thread item
    const newThreadItem: ThreadItem = {
      id: Date.now(),
      type: 'comment',
      from: 'You',
      content: commentText,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      avatar: 'YU'
    };

    // Add to message thread and update message list
    setMessageList(prev => 
      prev.map(msg => 
        msg.id === selectedMessage.id 
          ? { 
              ...msg, 
              thread: [...(msg.thread || []), newThreadItem],
              lastActivity: 'just now'
            }
          : msg
      )
    );

    // Update selected message to show new thread item
    setSelectedMessage(prev => prev ? {
      ...prev,
      thread: [...(prev.thread || []), newThreadItem],
      lastActivity: 'just now'
    } : null);

    // Clear the comment text
    setCommentText('');

    console.log('Comment added:', {
      messageId: selectedMessage.id,
      content: commentText,
      type: 'internal_comment'
    });
  };

  const handleDeleteMessage = () => {
    if (!selectedMessage) return;

    // Remove message from list
    setMessageList(prev => prev.filter(msg => msg.id !== selectedMessage.id));
    
    // Clear selected message
    setSelectedMessage(null);

    console.log('Message deleted:', selectedMessage.id);
  };

  const handleCreateTask = () => {
    if (!selectedMessage) return;

    // Create task data based on the message
    const taskData = {
      title: `Follow up on: ${selectedMessage.subject}`,
      taskType: 'Call',
      association: selectedMessage.company || selectedMessage.from,
      contactEmail: selectedMessage.fromEmail,
      messageId: selectedMessage.id,
      dueDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16) // Tomorrow
    };

    // Store task data in localStorage to pass to Tasks page
    localStorage.setItem('pendingTaskData', JSON.stringify(taskData));

    // Navigate to tasks page
    window.location.href = '/tasks';

    console.log('Task created for message:', selectedMessage.id);
  };



  const unreadCount = messageList.filter(msg => !msg.isRead).length;

  const filteredMessages = messageList.filter(message => {
    const matchesSearch = searchQuery === '' || 
      message.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.from.toLowerCase().includes(searchQuery.toLowerCase()) ||
      message.preview.toLowerCase().includes(searchQuery.toLowerCase());
    
    switch (activeFilter) {
      case 'unassigned':
        return !message.assignedTo && matchesSearch;
      case 'assigned-to-me':
        return (message.assignedTo === 'You' || message.assignedTo === 'Alan Chen') && matchesSearch;
      case 'all-open':
        return message.status === 'open' && matchesSearch;
      case 'closed':
        return message.status === 'closed' && matchesSearch;
      default:
        return matchesSearch;
    }
  });


  return (
    <div className="flex h-full bg-gray-50">
      {/* Left Sidebar - Filters */}
      <div className="w-64 bg-white border-r border-gray-200 flex flex-col">
        {/* Sidebar Header */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-semibold text-gray-900">Inbox</h1>
          </div>
          
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
        </div>

        {/* Filter List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-2">
            {inboxFilters.map((filter) => {
              const IconComponent = filter.icon;
              return (
                <button
                  key={filter.id}
                  onClick={() => setActiveFilter(filter.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm font-medium mb-1 ${
                    activeFilter === filter.id
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center">
                    <IconComponent className="w-4 h-4 mr-3" />
                    {filter.label}
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeFilter === filter.id
                      ? 'bg-blue-200 text-blue-800'
                      : 'bg-gray-200 text-gray-600'
                  }`}>
                    {filter.count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Middle Panel - Message List */}
      <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
        {/* Header with Actions */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-sm font-medium text-gray-900 capitalize">
                {inboxFilters.find(f => f.id === activeFilter)?.label}
              </h2>
              <p className="text-xs text-gray-500 mt-1">
                {filteredMessages.length} conversations • {unreadCount} unread
              </p>
            </div>
          </div>
        </div>

        {/* Message List */}
        <div className="flex-1 overflow-y-auto">
          {filteredMessages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full p-8 text-center">
              <Mail className="w-12 h-12 text-gray-400 mb-4" />
              <h3 className="text-sm font-medium text-gray-900 mb-2">No conversations</h3>
              <p className="text-xs text-gray-500">No conversations match your current filter</p>
            </div>
          ) : (
            filteredMessages.map((message) => (
            <div
              key={message.id}
                onClick={() => handleMessageClick(message)}
              className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                selectedMessage?.id === message.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                }`}
            >
              <div className="flex items-start space-x-3">
                {/* Avatar */}
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0">
                    {message.avatar}
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-sm ${!message.isRead ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'} truncate`}>
                      {message.from}
                    </p>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleImportant(message.id);
                        }}
                        className="hover:bg-gray-200 rounded p-1 -m-1"
                        title={message.isImportant ? 'Remove flag' : 'Add flag'}
                      >
                        <Flag className={`w-3 h-3 ${message.isImportant ? 'text-red-500' : 'text-gray-400'}`} />
                      </button>
                        <span className="text-xs text-gray-400">{message.timestamp}</span>
                      </div>
                    </div>
                    
                    <p className={`text-sm ${!message.isRead ? 'font-medium text-gray-900' : 'text-gray-600'} truncate mb-1`}>
                      {message.subject}
                    </p>
                    
                    <p className="text-xs text-gray-500 truncate mb-2">
                      {message.preview}
                    </p>

                    {/* Actions and Indicators */}
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex-1"></div>
                      <div className="flex items-center space-x-1">
                      {message.hasAttachment && (
                          <Paperclip className="w-3 h-3 text-gray-400" />
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStar(message.id);
                        }}
                        className="hover:text-yellow-500"
                          title={message.isStarred ? 'Remove star' : 'Add star'}
                      >
                          <Star className={`w-3 h-3 ${message.isStarred ? 'text-yellow-500 fill-current' : 'text-gray-400'}`} />
                      </button>
                    {!message.isRead && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                  </div>
                </div>
              </div>
            </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Panel - Message Detail */}
      <div className="flex-1 flex flex-col">
        {selectedMessage ? (
          <>
            {/* Message Header */}
            <div className="p-6 border-b border-gray-200 bg-white">
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-sm font-medium text-white">
                    {selectedMessage.avatar}
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-gray-900 mb-1">{selectedMessage.subject}</h2>
                    <div className="flex items-center space-x-4 text-sm text-gray-600 mb-2">
                      <span>From: {selectedMessage.from}</span>
                      <span>{selectedMessage.fromEmail}</span>
                      <span>{selectedMessage.timestamp}</span>
                    </div>
                    {selectedMessage.assignedTo && (
                      <div className="mt-1">
                        <span className="text-xs text-gray-600">
                          Assigned to {selectedMessage.assignedTo}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => toggleStar(selectedMessage.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                    title={selectedMessage.isStarred ? 'Remove star' : 'Add star'}
                  >
                    <Star className={`w-5 h-5 ${selectedMessage.isStarred ? 'text-yellow-500 fill-current' : ''}`} />
                  </button>
                  <button 
                    onClick={() => toggleImportant(selectedMessage.id)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
                    title={selectedMessage.isImportant ? 'Remove flag' : 'Add flag'}
                  >
                    <Flag className={`w-5 h-5 ${selectedMessage.isImportant ? 'text-red-500' : ''}`} />
                  </button>
                  <button 
                    onClick={handleDeleteMessage}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                    title="Delete message"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Message Content and Contact Info */}
            <div className="flex-1 flex overflow-hidden">
              {/* Message Content Area with Composition */}
              <div className="flex-1 flex flex-col bg-white">
            {/* Message Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  {/* Original Message */}
                  <div className="prose max-w-none mb-6">
                    <div className="whitespace-pre-wrap text-gray-900 text-sm leading-relaxed">
                  {selectedMessage.content}
                    </div>
                  </div>

                  {/* Thread Items */}
                  {selectedMessage.thread && selectedMessage.thread.length > 0 && (
                    <div className="space-y-4">
                      {/* Date Separator */}
                      <div className="flex items-center justify-center py-2">
                        <div className="text-xs text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                          September 12
                        </div>
                      </div>

                      {selectedMessage.thread.map((threadItem) => (
                        <div key={threadItem.id}>
                          {threadItem.type === 'email' ? (
                            /* Email Thread Item */
                            <div className="flex items-start space-x-3">
                              {/* Avatar */}
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0">
                                {threadItem.avatar}
                              </div>
                              
                              {/* Email Content */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2 mb-1">
                                  <span className="text-sm font-medium text-gray-900">
                                    {threadItem.from}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {threadItem.timestamp}
                                  </span>
                                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800">
                                    Email
                                  </span>
                                  {threadItem.isSent && (
                                    <span className="text-xs text-gray-500">
                                      To: {threadItem.fromEmail || selectedMessage.fromEmail}
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                  {threadItem.content}
                                </div>
                                {threadItem.isSent && (
                                  <div className="mt-2 text-xs text-gray-500">
                                    Sent at {threadItem.timestamp}
                                  </div>
                                )}
                              </div>
                              
                              {/* Actions */}
                              <div className="flex items-center space-x-1">
                                <button className="p-1 text-gray-400 hover:text-gray-600">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h.01M12 12h.01M19 12h.01" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ) : (
                            /* Comment Thread Item - HubSpot Style */
                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                              <div className="flex items-start space-x-3">
                                {/* Avatar */}
                                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0">
                                  {threadItem.avatar}
                                </div>
                                
                                {/* Comment Content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <span className="text-sm font-medium text-gray-900">
                                      {threadItem.from}
                                    </span>
                                    <span className="text-xs text-gray-600">
                                      left a comment
                                    </span>
                                    <span className="text-xs text-gray-500">
                                      • {threadItem.timestamp}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-600 mb-2 flex items-center">
                                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                    Only visible to your team
                                  </div>
                                  <div className="text-sm text-gray-900 whitespace-pre-wrap">
                                    {threadItem.content}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Email Composition Interface - HubSpot Style */}
                <div className="border-t border-gray-200">
                  {/* Email/Comment Tabs */}
                  <div className="px-6 py-2 bg-white border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex">
                        <button 
                          onClick={() => setActiveCompositionTab('email')}
                          className={`flex items-center px-3 py-2 text-sm font-medium ${
                            activeCompositionTab === 'email' 
                              ? 'text-gray-900 border-b-2 border-blue-600' 
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          <Mail className="w-4 h-4 mr-2" />
                          Email
                        </button>
                        <button 
                          onClick={() => setActiveCompositionTab('comment')}
                          className={`flex items-center px-3 py-2 text-sm font-medium ml-4 ${
                            activeCompositionTab === 'comment' 
                              ? 'text-gray-900 border-b-2 border-blue-600' 
                              : 'text-gray-500 hover:text-gray-700'
                          }`}
                        >
                          Comment
                        </button>
                </div>
              </div>
            </div>

                  {/* Contact Tag - Only show for Email tab */}
                  {activeCompositionTab === 'email' && (
                    <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                      <div className="flex items-center">
                        <div className="flex items-center bg-blue-100 text-blue-800 px-2 py-1 rounded text-sm">
                          <span>{selectedMessage.from}</span>
                          <button className="ml-2 text-blue-600 hover:text-blue-800">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Composition Area */}
                  <div className="p-6">
                    {/* Text Input */}
                    <textarea
                      value={activeCompositionTab === 'email' ? replyText : commentText}
                      onChange={(e) => activeCompositionTab === 'email' ? setReplyText(e.target.value) : setCommentText(e.target.value)}
                      placeholder={
                        activeCompositionTab === 'email' 
                          ? "Write a message."
                          : "Write a comment. This is only visible to your team."
                      }
                      className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      rows={6}
                      style={{ minHeight: '120px' }}
                    />

                    {/* Bottom Toolbar and Action Button */}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center space-x-2">
                        {/* Simplified Toolbar - Only functional items */}
                        {/* <button className="px-2 py-1 text-sm text-blue-600 hover:text-blue-700 font-medium">
                          Insert
                        </button> */}
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {activeCompositionTab === 'email' ? (
                          <button
                            onClick={handleSendReply}
                            disabled={!replyText.trim()}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                          >
                            Send
                            <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                </button>
                        ) : (
                  <button 
                            onClick={handleAddComment}
                            disabled={!commentText.trim()}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                            Add comment
                  </button>
                )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Info Sidebar */}
              <div className="w-80 border-l border-gray-200 bg-gray-50 p-6 overflow-y-auto">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">About this contact</h3>
                
                <div className="space-y-4">
                  {/* Contact Details */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex items-start space-x-3 mb-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-sm font-medium text-white flex-shrink-0">
                        {selectedMessage.avatar}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-gray-900 truncate">{selectedMessage.from}</p>
                        <p className="text-sm text-gray-600 truncate">{selectedMessage.fromEmail}</p>
                      </div>
                    </div>
                    
                    {selectedMessage.company && (
                      <div className="flex items-start space-x-2 text-sm text-gray-600 mb-2">
                        <Building className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <span className="break-words">{selectedMessage.company}</span>
                      </div>
                    )}
                    
                    <div className="flex items-start space-x-2 text-sm text-gray-600">
                      <Clock className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span className="break-words">Last activity: {selectedMessage.lastActivity}</span>
                    </div>
                  </div>

                  {/* Tags */}
                  {selectedMessage.tags && selectedMessage.tags.length > 0 && (
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                        <Tag className="w-4 h-4 mr-2 flex-shrink-0" />
                        Tags
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {selectedMessage.tags.map((tag, index) => (
                          <span key={index} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md break-words">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quick Actions */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Quick Actions</h4>
                    <div className="space-y-2">
                      <button 
                        onClick={handleCreateTask}
                        className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                        title="Create a follow-up task for this message"
                      >
                        <Plus className="w-4 h-4" />
                        <span>Create task</span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <Mail className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a conversation</h3>
              <p className="text-gray-500 max-w-sm">Choose a conversation from the list to view its contents and take action</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;